import { Typography } from 'antd';
import React, { forwardRef, useEffect, useRef, useState, useMemo } from 'react';
import styled from 'styled-components';
import StoryAudioReader from '../../components/StoryWithAi/StoryReader';
import ModalMenu from '../../components/Modals';
import SentryWrap from 'components/Sentry';

const { Title, Paragraph } = Typography;

const Container = styled.div`
  background-color: #341b55;
  color: white;
  padding: 10px 10px 10px 10px;
  text-align: center;
  position: relative;
  overflow: auto;

  @media print {
    background-color: white !important;
    color: black !important;
    padding: 35px !important;
    margin: 0;
    border: none;
    width: 100%;
    height: auto;
    overflow: visible;
    border-radius: 0px;
    page-break-before: auto;
    page-break-after: auto;

    .lesson-plan-container {
      page-break-inside: avoid;
    }
  }
`;

const StyledTitle = styled(Title)`
  color: white !important;
  font-weight: bold;
  text-align: center;
  margin-bottom: 2px !important;
  max-width: 85%;
  margin: 0 auto;
  font-size: 19px !important;

  @media screen and (max-width: 600px) {
    font-size: 15px !important;
  }
  @media print {
    color: black !important;
    font-size: 22px;
    margin-bottom: 10px !important;
  }
`;

const ContentWrapper = styled.div`
  margin: 0 auto;
  padding: 12px;
  overflow-y: auto;
  width: 100%;
  font-size: 18px;
  border-top: 0.5px dashed lightgray;
  padding-bottom: 135px;
  max-height: ${({ storyContainerHeight }) =>
    storyContainerHeight ? storyContainerHeight - 120 : 428}px;

  @media screen and (min-width: 991px) {
    max-height: ${({ storyContainerHeight }) =>
      storyContainerHeight ? storyContainerHeight - 220 : 428}px !important;
  }

  @media screen and (min-width: 601px) and (max-width: 800px) {
    max-height: ${({ storyContainerHeight }) =>
      storyContainerHeight ? storyContainerHeight - 220 : 303}px !important;
  }

  @media screen and (max-width: 991px) {
    max-height: ${({ storyContainerHeight }) => (!storyContainerHeight && 364)}px !important;
  }

  @media screen and (max-width: 760px) {
    max-height: ${({ storyContainerHeight }) => (!storyContainerHeight && 219)}px !important;
  }

  @media screen and (max-width: 475px) {
    max-height: 176px !important;
  }
  @media screen and (max-width: 400px) {
    max-height: 210px !important;
  }

  @media print {
    border-top: 0 !important;
    max-height: unset;
    overflow: visible;
    height: auto;
    padding: 0;
    margin: 0;
    font-size: 16px;
    orphans: 2;
    widows: 2;

    & > div {
      page-break-inside: auto !important;
      break-inside: auto !important;
    }
  }
`;

const StyledHtml = styled.span`
  b {
    text-decoration: underline;
  }
  .story_word.highlighted {
    background-color: yellow !important;
    color: #000 !important;
    border-radius: 3px;
    padding: 2px 0;
    transition: background-color 0.2s ease;
  }
`;

const StyledParagraph = styled(Paragraph)`
  color: white;
  text-align: left;
  line-height: 1.8;
  font-size: 18px;
  @media Screen and (max-width: 600px) {
    font-size: 14px !important;
  }
  @media print {
    color: black !important;
    line-height: 1.5;
    font-size: 16px;
    margin-bottom: 8px;
    orphans: 2;
    widows: 2;
  }
`;

const PrintContainerHidden = styled.div`
  @media print {
    visibility: hidden !important;
    display: none !important;
  }
`;

const PrintBlock = styled.div`
  @media print {
    padding-top: 10mm;
    padding-bottom: 4mm;
    page-break-inside: avoid;
    break-inside: avoid;
  }
`;

const escapeHtml = (text) => {
  if (!text) return '';
  const placeholders = [];
  let placeholderIndex = 0;
  let processedText = text.replace(/<b>.*?<\/b>/gi, (match) => {
    const placeholder = `__BOLD_PLACEHOLDER_${placeholderIndex}__`;
    placeholders[placeholderIndex] = match;
    placeholderIndex++;
    return placeholder;
  });
  const div = document.createElement('div');
  div.textContent = processedText;
  let escaped = div.innerHTML;
  placeholders.forEach((boldTag, index) => {
    escaped = escaped.replace(`__BOLD_PLACEHOLDER_${index}__`, boldTag);
  });
  return escaped;
};

const mapWord = (paragraphs, timestampData, images, isStream) => {
  let int_index = 0;
  let global_index = 0;

  const paraSentences = paragraphs?.join(" ").split(".");
  const result = [];
  let start = 0;
  let end = 0;
  let counter = 0;
  let stringPerSection = "";

  const wordPerSection = (paraSentences, startIndex, endIndex) => {
    let paraSlice = paraSentences.slice(startIndex,endIndex+1).join(".");
    if(paraSlice.length !=0){
      paraSlice = paraSlice.endsWith(".") ? paraSlice.split(" ") : `${paraSlice}.`.split(" ")
      return paraSlice
    }

  }

  for(let end=0; end<paraSentences.length; ){
      
      counter += paraSentences[end].trim().split(" ").length
      if(counter<=45){
          end += 1
      }else{
          stringPerSection = wordPerSection(paraSentences, start, end+1)
          result.push(stringPerSection)
          start=end+1
          counter = 0
      } 
  }
  stringPerSection = wordPerSection(paraSentences, start, end+1)
  result.push(stringPerSection)

  // const allWords = paragraphs?.join(' ').split(' ').filter((word) => word.trim()) || [];
  // const totalWords = allWords.length;
  // const totalSections = paragraphs?.length || 1;

  // const minWordsPerSection = 50;
  // const wordsPerSection = Math.max(minWordsPerSection, Math.ceil(totalWords / totalSections));

  // const wordSections = [];
  // for (let i = 0; i < totalSections; i++) {
  //   const startIndex = i * wordsPerSection;
  //   const endIndex = Math.min(startIndex + wordsPerSection, totalWords);
  //   const sectionWords = allWords.slice(startIndex, endIndex);
  //   wordSections.push(sectionWords);
  // }

  return paragraphs?.map((element, paraIndex) => {
    const sectionWords = result[paraIndex] || [];

    if (!sectionWords || sectionWords.length === 0) {
      return null;
    }

    const modifiedWords = sectionWords.map((word) => {
      const item = timestampData[global_index];
      const item_index = global_index;
      if (item) {
        const cleanWord = word.replace(/<[^>]*>/g, '').trim();
        if (item.value === cleanWord) {
          global_index++;
        } else if (item.value && item.value.includes(cleanWord)) {
          const arr = item.value.split(' ');
          if (arr.length === 1 || arr[arr.length - 1] === cleanWord) {
            global_index++;
          }
        } else {
          global_index++;
        }
      }

      const currentIndex = ++int_index;
      let processedWord = word;

      const openBoldCount = (word.match(/<b>/g) || []).length;
      const closeBoldCount = (word.match(/<\/b>/g) || []).length;

      if (openBoldCount > closeBoldCount) {
        const missingCloses = openBoldCount - closeBoldCount;
        processedWord = processedWord + '</b>'.repeat(missingCloses);
      } else if (closeBoldCount > openBoldCount) {
        const missingOpens = closeBoldCount - openBoldCount;
        processedWord = '<b>'.repeat(missingOpens) + processedWord;
      }

      const escapedWord = escapeHtml(processedWord);
      const sanitizedWordForClass = word.replace(/<[^>]*>/g, '').replace(/["']/g, '-');

      return `<span style="cursor:pointer;" data-start="${currentIndex}" data-index="${item_index}" class="story_word ${item_index}-${sanitizedWordForClass}" id="${item_index}_word">${escapedWord}</span>`;
    });

    if (isStream || images?.length === 0 && paraIndex < 3) {
      modifiedWords.unshift(
        `<span class="image-skeleton-placeholder">
          <label>Generating images...</label>
          <img src="/src/utils/assets/images/placeholderImg.png" alt="" title="" />
        </span>`,
      );
    } else if (images && images[paraIndex]) {
      modifiedWords.unshift(
        `<img src="${images[paraIndex]}" alt="Story illustration for paragraph ${paraIndex + 1}" class="aiGenerateImg story-inline-img" data-image-src="${images[paraIndex]}" style="display:block;max-width:100%;border-radius:18px;margin:10px auto 10px auto;border:6px solid black;box-shadow:0 4px 10px rgba(0,0,0,0.2);cursor:pointer;" />`,
      );
    }
    return modifiedWords.join(' ');
  }).filter(Boolean);
};

const StreamingCursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 1.2em;
  background-color: #fff;
  margin-left: 2px;
  animation: blink 1s infinite;
  vertical-align: text-bottom;

  @keyframes blink {
    0%, 50% {
      opacity: 1;
    }
    51%, 100% {
      opacity: 0;
    }
  }
`;

const StreamingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  font-size: 14px;
  padding: 10px;
  margin-top: 10px;
  opacity: 0.8;

  .streaming-dot {
    width: 8px;
    height: 8px;
    background-color: #4caf50;
    border-radius: 50%;
    animation: pulse 1.5s ease-in-out infinite;

    @keyframes pulse {
      0%, 100% {
        opacity: 1;
        transform: scale(1);
      }
      50% {
        opacity: 0.5;
        transform: scale(1.2);
      }
    }
  }
`;

const SoyHistoriasViewAdmin = forwardRef(
  ({ text, images, title, storyInfo, imageConfiguration, isStream = false }, ref) => {
    const storyContainerHeight = imageConfiguration?.clientHeight;
    const [timestampData, setTimestampData] = useState([]);
    const [audioVersion, setAudioVersion] = useState('female');
    const last_index = useRef(0);
    const audioRef = useRef();
    const contentWrapperRef = useRef(null);
    const [isStreamingActive, setIsStreamingActive] = useState(false);
    const prevTextLength = useRef(0);
    const [imageModal, setImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    
    const paragraphs = useMemo(() => {
      return mapWord(text?.split('\n').filter(Boolean), timestampData, images, isStream);
    }, [text, timestampData, images, isStream]);

    useEffect(() => {
      if (isStream && text) {
        const currentLength = text.length;
        if (currentLength > prevTextLength.current) {
          setIsStreamingActive(true);
          prevTextLength.current = currentLength;
          
          // Reset streaming indicator after a short delay if no new content
          const timeout = setTimeout(() => {
            setIsStreamingActive(false);
          }, 2000);
          
          return () => clearTimeout(timeout);
        }
      } else {
        setIsStreamingActive(false);
        prevTextLength.current = 0;
      }
    }, [text, isStream]);

    function getTimeStamp(text) {
      try {
        const value = JSON.parse(text);
        if (Array.isArray(value)) {
          return value;
        }
        const lines = text.trim().split('\n').map((line) => JSON.parse(line));
        return lines;
      } catch (error) {
        const lines = text.trim().split('\n').map((line) => JSON.parse(line));
        return lines;
      }
    }

    useEffect(() => {
      const loadTimestamps = async () => {
        const url =
          audioVersion === 'male' ? storyInfo?.male_audio_timestamp : storyInfo?.female_audio_timestamp;
        if (!url) return setTimestampData([]);

        try {
          const res = await fetch(url);
          const text = await res.text();
          const lines = getTimeStamp(text);
          setTimestampData(lines);
        } catch (err) {
          console.error('Error loading timestamp JSON:', err);
          setTimestampData([]);
        }
      };
      loadTimestamps();
    }, [storyInfo, audioVersion]);

    const handleAudioProgress = (time) => {
      if (!timestampData || timestampData.length === 0) return;
      
      let currentIndex = timestampData.findIndex(
        (entry, idx) =>
          entry.time <= time && (idx === timestampData.length - 1 || timestampData[idx + 1]?.time > time),
      );
      
      if (currentIndex === -1) {
        if (time >= timestampData[timestampData.length - 1]?.time) {
          currentIndex = timestampData.length - 1;
        } else {
          return;
        }
      }
      
      if (currentIndex === last_index.current) return;
      
      document.querySelectorAll('.story_word.highlighted').forEach((el) => {
        el.classList.remove('highlighted');
      });

      const item = document.getElementById(`${currentIndex}_word`);
      last_index.current = currentIndex;

      if (item) {
        item.classList.add('highlighted');
        if (contentWrapperRef.current) {
          const container = contentWrapperRef.current;
          const containerTop = container.getBoundingClientRect().top;
          const itemTop = item.getBoundingClientRect().top;
          const offset = itemTop - containerTop;
        }
      }
    };

    useEffect(() => {
      const handleClick = (e) => {
        const imageElement = e.target.closest('.story-inline-img, .aiGenerateImg');
        if (imageElement) {
          const imageSrc = imageElement.getAttribute('data-image-src') || imageElement.getAttribute('src');
          if (imageSrc) {
            setSelectedImage(imageSrc);
            setImageModal(true);
            return;
          }
        }

        const wordElement = e.target.closest('.story_word');
        if (!wordElement) return;

        const index = parseInt(wordElement.getAttribute('data-index'), 10);
        if (timestampData[index] && audioRef.current?.setTime) {
          const time = timestampData[index].time;
          audioRef.current.setTime(time);
        }
      };

      const container = contentWrapperRef.current;
      if (container) container.addEventListener('click', handleClick);
      return () => {
        if (container) container.removeEventListener('click', handleClick);
      };
    }, [timestampData]);

    return (
      <SentryWrap>
        <style
          dangerouslySetInnerHTML={{
            __html: `
          @page {
            margin: 15mm 10mm 28mm 10mm;
            size: auto;
          }
          .story_word.highlighted {
            background-color: yellow !important;
            color: #000 !important;
            border-radius: 3px;
            padding: 2px 0;
          }
          @media print {
            html, body {
              overflow: visible !important;
              height: auto !important;
            }
            .printable, .printable *:not(img) {
              overflow: visible !important;
              max-height: none !important;
            }
            .highlighted {
              background-color: transparent !important;
              color: inherit !important;
            }
            .aiGenerateImg,
            .story-inline-img {
              border-radius: 18px !important;
              border: 6px solid #000 !important;
              box-shadow: 0 4px 10px rgba(0,0,0,0.2) !important;
              max-width: 100% !important;
              margin: 10px auto !important;
              display: block !important;
            }
          }
          `,
          }}
        />
        <Container className="1313 to_be_print printable" ref={ref}>
          <StyledTitle level={2}>{title || ''}</StyledTitle>
          <PrintContainerHidden>
            <StoryAudioReader
              id={storyInfo?.id}
              ref={audioRef}
              male_audio={isStream ? '' : storyInfo?.male_audio}
              female_audio={isStream ? '' : storyInfo?.female_audio}
              player={true}
              onTimeUpdate={handleAudioProgress}
              onVoiceChange={(version) => {
                last_index.current = 0;
                setAudioVersion(version);
              }}
              paragraph={paragraphs}
            />
          </PrintContainerHidden>

          <ContentWrapper
            className="aiGenerateUpr"
            storyContainerHeight={storyContainerHeight}
            ref={contentWrapperRef}
          >
            {paragraphs?.map((para, index) => (
              <PrintBlock key={index} className="aiGenerateTxt">
                <StyledParagraph>
                  <StyledHtml dangerouslySetInnerHTML={{ __html: para }} />
                  {isStream && index === paragraphs.length - 1 && (
                    <StreamingCursor />
                  )}
                </StyledParagraph>
              </PrintBlock>
            ))}
            {isStream && isStreamingActive && (
              <StreamingIndicator>
                <div className="streaming-dot" />
                <span>Streaming content...</span>
              </StreamingIndicator>
            )}
          </ContentWrapper>
        </Container>
        <ModalMenu
          type="imageModal"
          image={selectedImage}
          open={imageModal}
          close={() => {
            setImageModal(false);
            setSelectedImage(null);
          }}
        />
      </SentryWrap>
    );
  },
);

export default SoyHistoriasViewAdmin;

